<template>
	<AppDialog dialogName="FeatureAppearanceSettings" closeRight="-390">
		<div class="pop-up-content-container dialog-boarders display-flex flex-row" :class="{ 'is-desktop-view': isDesktopView }">
			<DesktopMobileToggle
				:isDesktop="isDesktopView"
				class="desktop-mobile-toggle-container"
				@onToggleClick="isDesktopView = !isDesktopView"
			/>
			<div
				:style="{ 'background-image': backgroundImage, 'background-color': backgroundColor }"
				class="image-container display-flex flex-column flex-2 height-100 flex-centerize"
			>
				<div class="widget-demo-container flex-centerize height-100 flex-column">
					<div class="skeleton-container">
						<div class="logo-container width-100 display-flex flex-row flex-justify-start">
							<WidgetFormLogo class="mb-3" :src="logoSrc" :isActive="isLogoActive" />
						</div>
						<SIcon v-if="isDesktopView" :width="skeletonWidth" name="widget-skeleton" />
						<div v-else class="flex-centerize"><SIcon name="widget-skeleton-mobile" /></div>
					</div>
				</div>
				<div v-if="isDesktopView" class="px-2 pb-3 width-100 display-flex flex-row flex-align-end flex-justify-space-between">
					<div>
						<SIcon v-if="showProgressStatus" name="flow-run-progress-skeleton" />
						<SIcon v-else-if="showLoaderWithStatus" name="flow-run-loader-and-debugger" />
					</div>
					<div v-if="!isWhiteLabelBrandingEnabled">
						<AppIcon class="ml-1" name="powered-by-flow-run-outlined" />
					</div>
				</div>
			</div>
			<div class="settings-container display-flex flex-column flex-1 height-100">
				<FeatureAppearanceSideBar />
			</div>
		</div>
	</AppDialog>
</template>

<script>
import { mapGetters } from 'vuex';

import { BackgroundMode, ProgressBarSettings } from '@ply-io/ply-front-shared-utilities';

import DesktopMobileToggle from './DesktopMobileToggle/DesktopMobileToggle';
import FeatureAppearanceSideBar from './FeatureAppearanceSideBar';
import WidgetFormLogo from '@/components/BlockEditorWidget/WidgetFormLogo';
import AppDialog from '@/components/dialogs/AppDialog/AppDialog';
import { getWorkflowAppearanceSettings } from '@/store/modules/selectedWorkflow/getters.js';
import { isBackgroundColor } from '@/utils/helpers.js';
import { FEATURE_BACKGROUND_IMAGE_SUPPORTED_TYPES } from '@/utils/workflow-service.const';

export default {
	name: 'FeatureAppearanceSettings',
	components: {
		AppDialog,
		WidgetFormLogo,
		FeatureAppearanceSideBar,
		DesktopMobileToggle,
	},
	computed: {
		...mapGetters([getWorkflowAppearanceSettings.fullPath]),
		skeletonWidth() {
			return this.$vuetify.breakpoint.width > 1600 ? 538 : 400;
		},
		workflowAppearanceSettings() {
			return this[getWorkflowAppearanceSettings.fullPath];
		},
		isLogoActive() {
			return this.workflowAppearanceSettings.logo_enabled;
		},
		isWhiteLabelBrandingEnabled() {
			return this.workflowAppearanceSettings.widgets_white_label_branding_enabled;
		},
		logoSrc() {
			return this.workflowAppearanceSettings.logo;
		},
		progressBarMode() {
			return this.workflowAppearanceSettings.progress_bar || ProgressBarSettings.Simple;
		},
		showProgressStatus() {
			return this.progressBarMode === ProgressBarSettings.Regular;
		},
		showLoaderWithStatus() {
			return this.progressBarMode === ProgressBarSettings.Debugger;
		},
		backgroundMode() {
			return this.workflowAppearanceSettings.background_mode || BackgroundMode.Off;
		},
		backgroundImageSrc() {
			if (this.backgroundMode === BackgroundMode.Image) {
				return this.workflowAppearanceSettings.background || this.workflowAppearanceSettings.background_file;
			} else {
				return this.workflowAppearanceSettings.background;
			}
		},
		isUploadedImage() {
			return (
				this.backgroundImageSrc &&
				FEATURE_BACKGROUND_IMAGE_SUPPORTED_TYPES.some((imageSuffix) => this.backgroundImageSrc.includes(imageSuffix))
			);
		},
		backgroundImage() {
			if (!this.backgroundImageSrc || this.backgroundMode !== BackgroundMode.Image || isBackgroundColor(this.backgroundImageSrc)) {
				return '';
			}
			if (!this.workflowAppearanceSettings.mobile_background_enabled && !this.isDesktopView) {
				return '';
			}
			return this.isUploadedImage
				? 'url(' + this.backgroundImageSrc + ')'
				: 'url(' + require('../../../assets/png/' + this.backgroundImageSrc + '.png') + ')';
		},
		backgroundColor() {
			if (
				!this.backgroundImageSrc ||
				this.backgroundMode !== BackgroundMode.BackgroundColor ||
				!isBackgroundColor(this.backgroundImageSrc)
			) {
				return 'transparent';
			}
			return this.backgroundImageSrc;
		},
	},

	data() {
		return { isDesktopView: true };
	},
};
</script>

<style scoped lang="scss">
.dialog-boarders {
	border-radius: 15px;
	background-color: white;
	box-shadow: 0px 3px 6px var(--shadow-color);
}

.pop-up-content-container {
	position: relative;
	width: 1440px;
	height: 90vh;
	max-height: 900px;
	min-height: 600px;
	margin-left: -400px;

	&:not(.is-desktop-view) {
		.logo-container {
			position: absolute;
			top: 20px;
			left: 10px;
		}
	}
}

.desktop-mobile-toggle-container {
	position: absolute;
	top: 20px;
	left: 20px;
}

.settings-container {
	border-left: 1px solid var(--border-color);
}

.widget-demo-container {
	.skeleton-container {
		position: relative;
	}
}
.image-container {
	border-radius: 10px 0px 0px 10px;
}
</style>
